import { useState } from 'react';
import {
  IonSpinner,
  IonText,
  IonIcon,
  IonButton,
  useIonToast,
} from '@ionic/react';
import { useQuery, useMutation } from '@apollo/client/react';
import { GET_FANTASY_LEAGUE_MEMBER_PLAYERS } from '../../../../graphql/queries/fantasyLeagueMemberPlayers';
import { GET_TEAM_TAB_DATA } from '../../../../graphql/queries/teamTabData';
import { UPDATE_LINEUP } from '../../../../graphql/mutations/updateLineup';
import { PlayerSelectionModal } from './PlayerSelectionModal';
import { LineupTimeCounter } from './LineupTimeCounter';
import { LineupService, ROLE_LABELS, type LineupSlot, type RosterTeamPlayer } from '../../services/LineupService';
import { LineupPitch } from '../../components/lineup-editor/LineupPitch';
import { TacticsSelector } from '../../components/lineup-editor/TacticsSelector';
import type {
  GetFantasyLeagueMemberPlayersQuery,
  GetFantasyLeagueMemberPlayersQueryVariables,
  UpdateLineupMutation,
  UpdateLineupMutationVariables,
} from '../../../../types/graphql-generated';
import { closeOutline, checkmarkCircle, warningOutline, closeCircle } from 'ionicons/icons';
import { posthog } from '../../../../lib/posthog';

interface LineupEditorProps {
  memberId: string;
  leagueId?: string;
  initialTactics?: string | null;
  fantasyTeamPlayers?: RosterTeamPlayer[];
}

const EMPTY_ROSTER: RosterTeamPlayer[] = [];

interface ValidationResult {
  isValid: boolean;
  emptySlots: number;
  roleExceeds: { role: string; roleLabel: string; current: number; allowed: number; }[];
  playersWithoutTeam: { position: number; playerNick: string; }[];
}

const validateLineup = (lineup: LineupSlot[], tactics: string): ValidationResult => {
  const distribution = LineupService.parseTactics(tactics);
  const roleCounts: Record<string, number> = {
    'PORTERO': 0,
    'DEFENSA': 0,
    'CENTROCAMPISTA': 0,
    'DELANTERO': 0
  };

  let emptySlots = 0;
  const playersWithoutTeam: ValidationResult['playersWithoutTeam'] = [];

  lineup.forEach((slot) => {
    if (!slot.fantasyTeamPlayer) {
      emptySlots++;
    } else {
      const role = slot.fantasyTeamPlayer.player.role;
      if (role) {
        roleCounts[role]++;
      }

      if (!slot.fantasyTeamPlayer.player.team) {
        playersWithoutTeam.push({
          position: slot.position,
          playerNick: slot.fantasyTeamPlayer.player.nick
        });
      }
    }
  });

  const roleExceeds: ValidationResult['roleExceeds'] = [];
  Object.entries(roleCounts).forEach(([role, count]) => {
    if (count > distribution[role]) {
      roleExceeds.push({
        role,
        roleLabel: ROLE_LABELS[role],
        current: count,
        allowed: distribution[role]
      });
    }
  });

  const isValid = emptySlots === 0 && roleExceeds.length === 0;

  return { isValid, emptySlots, roleExceeds, playersWithoutTeam };
};

export const LineupEditor = ({
  memberId,
  leagueId,
  initialTactics,
  fantasyTeamPlayers,
}: LineupEditorProps) => {
  const hasProvidedTeamData = fantasyTeamPlayers !== undefined;
  const { data, loading, error } = useQuery<
    GetFantasyLeagueMemberPlayersQuery,
    GetFantasyLeagueMemberPlayersQueryVariables
  >(GET_FANTASY_LEAGUE_MEMBER_PLAYERS, {
    variables: { fantasyLeagueMemberId: memberId },
    skip: hasProvidedTeamData,
  });

  const roster = fantasyTeamPlayers ?? data?.fantasyLeagueMemberPlayers?.fantasyTeamPlayers ?? EMPTY_ROSTER;
  const currentTactics = hasProvidedTeamData
    ? initialTactics
    : data?.fantasyLeagueMemberPlayers?.tactics;

  const [tactics, setTactics] = useState<string>(() =>
    currentTactics || '1-4-4-2'
  );
  const [lineup, setLineup] = useState<LineupSlot[]>(() =>
    LineupService.initializeLineupSlotsFromFantasyTeamPlayers(roster)
  );
  const [isDirty, setIsDirty] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mutationErrors, setMutationErrors] = useState<string[]>([]);
  const [presentToast] = useIonToast();

  const [updateLineup, { loading: saving }] = useMutation<
    UpdateLineupMutation,
    UpdateLineupMutationVariables
  >(UPDATE_LINEUP, {
    refetchQueries: leagueId
      ? [ { query: GET_TEAM_TAB_DATA, variables: { fantasyLeagueId: leagueId } } ]
      : [ { query: GET_FANTASY_LEAGUE_MEMBER_PLAYERS, variables: { fantasyLeagueMemberId: memberId } } ],
  });

  // Synchronize local editing state when the parent query or fallback query changes.
  const [previousRoster, setPreviousRoster] = useState(roster);
  const [previousTactics, setPreviousTactics] = useState(currentTactics);
  if (previousRoster !== roster || previousTactics !== currentTactics) {
    setPreviousRoster(roster);
    setPreviousTactics(currentTactics);
    setLineup(LineupService.initializeLineupSlotsFromFantasyTeamPlayers(roster));
    setIsDirty(false);
    setTactics(currentTactics || '1-4-4-2');
  }

  const handleTacticsChange = (newTactics: string) => {
    setTactics(newTactics);
    const updatedLineup = LineupService.updateLineupFromTactic(lineup, newTactics);
    setLineup(updatedLineup);
    setIsDirty(true);
  };

  const handleSlotClick = (position: number) => {
    setSelectedPosition(position);
    setIsModalOpen(true);
  };

  const handlePlayerSelect = (fantasyTeamPlayer: RosterTeamPlayer | null) => {
    if (selectedPosition === null) return;

    const updatedLineup = [...lineup];
    const index = selectedPosition - 1;

    updatedLineup[index] = {
      position: selectedPosition,
      fantasyTeamPlayerId: fantasyTeamPlayer ? fantasyTeamPlayer.id : null,
      fantasyTeamPlayer,
    };

    setLineup(updatedLineup);
    setIsDirty(true);
    setIsModalOpen(false);
    setSelectedPosition(null);
  };

  const handleSave = async () => {
    // Clear previous errors
    setMutationErrors([]);

    // Build lineup players array for mutation
    const lineupPlayers = lineup
      .filter((slot) => slot.fantasyTeamPlayer && slot.fantasyTeamPlayerId)
      .map((slot) => ({
        fantasyTeamPlayerId: slot.fantasyTeamPlayerId!,
        startingPosition: slot.position,
      }));

    try {
      const result = await updateLineup({
        variables: {
          input: {
            fantasyLeagueMemberId: memberId,
            tactics,
            lineupPlayers,
          },
        },
      });

      // Check for mutation errors
      if (result.data?.updateLineup?.errors && result.data.updateLineup.errors.length > 0) {
        setMutationErrors(result.data.updateLineup.errors);
        return; // Don't mark as clean if there are errors
      }

      if (result.data?.updateLineup?.errors.length === 0) {
        posthog.capture('lineup_saved', { tactics });
        setIsDirty(false);
        presentToast({
          message: 'Alineación guardada correctamente',
          duration: 3000,
          position: 'bottom',
          positionAnchor: 'app-footer',
          cssClass: 'toast-success',
          buttons: [{ icon: closeOutline, role: 'cancel' }],
        })
      }
    } catch (err) {
      console.error('Error updating lineup:', err);
      presentToast({
          message: 'No se pudo guardar la alineación',
          duration: 3000,
          position: 'bottom',
          positionAnchor: 'app-footer',
          cssClass: 'toast-danger',
          buttons: [{ icon: closeOutline, role: 'cancel' }],
      })
    }
  };

  // Get available fantasy team players for selection (exclude already selected)
  const getAvailablePlayersForPosition = (position: number): RosterTeamPlayer[] => {
    const allPlayers = roster;

    const selectedFantasyTeamPlayerIds = new Set(
      lineup
        .filter((slot) => slot.fantasyTeamPlayer && slot.position !== position)
        .map((slot) => slot.fantasyTeamPlayer!.id)
    );

    return allPlayers.filter((ftp) => !selectedFantasyTeamPlayerIds.has(ftp.id));
  };

  if (!hasProvidedTeamData && loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <IonSpinner name="crescent" />
      </div>
    );
  }

  if (!hasProvidedTeamData && error) {
    return (
      <IonText color="danger">
        <p>Error al cargar la alineación</p>
      </IonText>
    );
  }

  const selectedSlot = selectedPosition ? lineup[selectedPosition - 1] : null;
  const requiredRoleEnum = selectedPosition
    ? LineupService.getRoleForPosition(selectedPosition, tactics)
    : '';
  const requiredRoleLabel = requiredRoleEnum ? ROLE_LABELS[requiredRoleEnum] : '';

  return (
    <div className="space-y-4">
      <LineupTimeCounter />

      <IonButton
        expand="block"
        onClick={handleSave}
        disabled={!isDirty || saving}
        data-testid="save-lineup-button"
      >
        {saving ? <IonSpinner name="crescent" /> : 'Guardar alineación'}
      </IonButton>

      {/* Mutation Errors - Dismissible */}
      {mutationErrors.length > 0 && (
        <div className="bg-red-50 border border-red-300 rounded p-3 mb-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <IonIcon icon={closeCircle} className="text-red-600 text-xl" />
                <h3 className="font-semibold text-red-800">Errores al guardar</h3>
              </div>
              <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                {mutationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
            <button
              onClick={() => setMutationErrors([])}
              className="ml-2 text-red-600 hover:text-red-800"
              aria-label="Cerrar errores"
            >
              <IonIcon icon={closeCircle} className="text-xl" />
            </button>
          </div>
        </div>
      )}

      {/* Tactics and Validation Section */}
      <div className="flex flex-col md:flex-row gap-4 mb-4">
        {/* Left: Tactics Selector */}
        <div className="w-auto max-w-xs mx-auto md:mx-0 md:flex-1 text-center md:text-left">
          <TacticsSelector tactics={tactics} onChange={handleTacticsChange} />
        </div>

        {/* Right: Validation Info */}
        <div className="md:flex-1 space-y-2">
          {(() => {
            const validation = validateLineup(lineup, tactics);

            if (validation.isValid && validation.playersWithoutTeam.length === 0) {
              return (
                <div className="flex items-start gap-2 p-2 bg-green-50 border border-green-300 rounded text-sm">
                  <IonIcon icon={checkmarkCircle} className="text-green-600 text-lg flex-shrink-0 mt-0.5" />
                  <span className="text-green-800">
                    {isDirty
                      ? 'La alineación es válida, ¡¡ RECUERDA GUARDARLA !!'
                      : 'La alineación es válida'
                    }
                  </span>
                </div>
              );
            }

            return (
              <>
                {validation.emptySlots > 0 && (
                  <div className="flex items-start gap-2 p-2 bg-yellow-50 border border-yellow-300 rounded text-sm">
                    <IonIcon icon={warningOutline} className="text-yellow-600 text-lg flex-shrink-0 mt-0.5" />
                    <span className="text-yellow-800">
                      Hay posiciones libres en la alineación
                    </span>
                  </div>
                )}

                {validation.roleExceeds.map((exceed) => (
                  <div
                    key={exceed.role}
                    className="flex items-start gap-2 p-2 bg-yellow-50 border border-yellow-300 rounded text-sm"
                  >
                    <IonIcon icon={warningOutline} className="text-yellow-600 text-lg flex-shrink-0 mt-0.5" />
                    <span className="text-yellow-800">
                      Hay más {exceed.roleLabel.toLowerCase()}s de los permitidos ({exceed.current}/{exceed.allowed})
                    </span>
                  </div>
                ))}

                {validation.playersWithoutTeam.length > 0 && (
                  <div className="flex items-start gap-2 p-2 bg-yellow-50 border border-yellow-300 rounded text-sm">
                    <IonIcon icon={warningOutline} className="text-yellow-600 text-lg flex-shrink-0 mt-0.5" />
                    <span className="text-yellow-800">
                      Alguno de tus jugadores ya no está en la liga. La alineación podrá guardarse, pero este jugador sumará 0 puntos
                    </span>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>

      <LineupPitch lineup={lineup} tactics={tactics} onSlotClick={handleSlotClick} />

      {/* Player Selection Modal */}
      {selectedPosition && (
        <PlayerSelectionModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedPosition(null);
          }}
          onSelect={handlePlayerSelect}
          requiredRole={requiredRoleEnum}
          requiredRoleLabel={requiredRoleLabel}
          availablePlayers={getAvailablePlayersForPosition(selectedPosition)}
          currentSelection={selectedSlot?.fantasyTeamPlayer || null}
        />
      )}


    </div>
  );
};
